import { Button } from '@/components/shadcn/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { IconEditOutline } from '@icons/IconEditOutline'
import { IconMenuOutline } from '@icons/IconMenuOutline'
import { IconLinkOutline } from '@icons/IconLinkOutline'
import { toast } from 'sonner'
import { AiGatewayForm } from '../../Forms/AiGatewayForm'
import { AiGatewayLinkForm } from '../../Forms/AiGatewayLinkForm'
import { useCurrentOrganisation } from '../../useCurrentOrganisation'

type ExistingAiGateway = RestSchemas['Messages.AiGateway.ExistingAiGateway']

export function RowActionsMenu({ gateway }: { gateway: ExistingAiGateway }) {
  const { id: orgId } = useCurrentOrganisation()
  const { getAiGatewayApiListGateways, deleteAiGatewayApiDeleteGateway, deleteAiGatewayApiDeleteLink } =
    useRestClient()

  const invalidate = () =>
    getAiGatewayApiListGateways.invalidateQueries({
      parameters: { query: { orgId } },
    })

  const { mutate: deleteGateway } = deleteAiGatewayApiDeleteGateway.useMutation(undefined, {
    onSuccess: () => {
      invalidate()
      toast.success('AI Gateway deleted')
    },
    onError: () => toast.error('Failed to delete AI Gateway'),
  })

  const { mutate: deleteLink } = deleteAiGatewayApiDeleteLink.useMutation(undefined, {
    onSuccess: () => {
      invalidate()
      toast.success('Link removed')
    },
    onError: () => toast.error('Failed to remove link'),
  })

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" className="size-8">
          <IconMenuOutline size={16} />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        onCloseAutoFocus={(e) => {
          e.preventDefault()
        }}
      >
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <AiGatewayForm
          selectedGateway={gateway}
          sheetTitle="Edit AI Gateway"
          sheetDescription="Update name, provider, allowed models, or rotate the API key."
          onSubmit={invalidate}
          sheetTrigger={
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              <div className="flex items-center gap-2">
                <IconEditOutline size={16} />
                <span>Edit gateway</span>
              </div>
            </DropdownMenuItem>
          }
        />
        <AiGatewayLinkForm
          gateway={gateway}
          onSubmit={invalidate}
          sheetTrigger={
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              <div className="flex items-center gap-2">
                <IconLinkOutline size={16} />
                <span>Add link</span>
              </div>
            </DropdownMenuItem>
          }
        />
        {(gateway.links ?? []).map((link) => (
          <ConfirmDialog
            key={link.id}
            title="Remove link"
            description="Unlink this target from the AI Gateway?"
            onConfirm={() => {
              if (gateway.id == null || link.id == null) {
                toast.error('Unable to remove link: missing ID')
                return
              }
              deleteLink({
                path: { gatewayId: gateway.id, linkId: link.id },
              })
            }}
            trigger={
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                Unlink{' '}
                {link.workspaceLabel ??
                  link.applicationName ??
                  link.projectName ??
                  `link ${link.id}`}
              </DropdownMenuItem>
            }
          />
        ))}
        <ConfirmDialog
          title="Delete AI Gateway"
          description="Delete this gateway and its Key Vault secret? Existing links will be removed."
          confirmVariant="destructive"
          onConfirm={() => {
            if (gateway.id == null) {
              toast.error('Unable to delete AI Gateway: missing gateway ID')
              return
            }
            deleteGateway({
              path: { gatewayId: gateway.id },
            })
          }}
          trigger={
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              <div className="flex items-center gap-2">
                <IconDeleteOutline size={16} />
                <span>Delete gateway</span>
              </div>
            </DropdownMenuItem>
          }
        />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
